import {
  createPrimerCheckoutSession,
  getPaymentFailUrl,
  getPaymentSuccessUrl,
} from "../../../common/js/payments"
import { generateUpsellPageUrl, getUpsellOffers } from "../../../common/js/upsells"
import { getVariantPageUrl } from "../../../common/js/helpers"
import { TrackingManager } from "../../../common/js/services/tracking/trackingManager"

export default ({
  upsellOfferType,
  paymentsPath,
  paymentsFailPath,
  paymentsSuccessPath,
  chancePath,
  sendOffPath,
} = {}) => ({
  isLoading: null,
  upsell: null,
  internalTracker: new TrackingManager(
    window.trackingConfig.enabledTrackingServices,
    pipelineConfig,
  ),

  upsellEventName: {
    shopping_list_and_premium: {
      accept: "Premium & Shopping List Accepted Clicked",
      reject: "Premium & Shopping List Rejected Clicked",
      continue: "Continue Clicked",
    },
    shopping_list: {
      accept: "Shopping List Accepted Clicked",
      reject: "Shopping List Rejected Clicked",
      continue: "Continue Clicked",
    },
    premium: {
      accept: "Premium Accepted Clicked",
      reject: "Premium Rejected Clicked",
      continue: "Continue Clicked",
    },
    chat: {
      accept: "Chat Accepted Clicked",
      reject: "Chat Rejected Clicked",
      continue: "Continue Clicked",
    },
  },

  init() {
    this.commonInitUpsell()
  },

  commonInitUpsell() {
    this.store = Alpine.store("data")

    this.upsell = pipelineConfig.offers.find(
      (offer) => offer.type && offer.type.toLowerCase() === upsellOfferType.toLowerCase(),
    )

    if (!this.upsell) return

    this.store.values.activeOffer = this.upsell

    // Remove offer from skipped if user returned
    this.store.values.skippedUpsellOffers = this.store.values.skippedUpsellOffers.filter(
      (type) => type.toLowerCase() !== upsellOfferType.toLowerCase(),
    )
  },

  getNextUpsellOfferUrl() {
    const upsellOffers = getUpsellOffers(pipelineConfig.offers)

    const currentUpsellOfferIndex = upsellOffers.findIndex(
      (upsellOffer) => upsellOffer.type.toLowerCase() === upsellOfferType.toLowerCase(),
    )

    let nextUpsellOffer = upsellOffers[currentUpsellOfferIndex + 1]

    // Bundle should be shown only if both Premium and Shopping list upsells are skipped.
    // If either one of them is purchased, bundle should not be offered.
    if (nextUpsellOffer?.type.toLowerCase() === "shopping_list_and_premium") {
      if (
        !this.store.values.skippedUpsellOffers.includes("premium") ||
        !this.store.values.skippedUpsellOffers.includes("shopping_list")
      ) {
        // Skip current next
        nextUpsellOffer = upsellOffers[currentUpsellOfferIndex + 2]
      }
    }

    if (nextUpsellOffer) {
      return generateUpsellPageUrl({
        upsellOfferType: nextUpsellOffer.type,
      })
    } else {
      return this.goToSendOff()
    }
  },

  handleAcceptOffer(callbackFn) {
    if (this.selectedExpert === "no_coach") {
      this.internalTracker.trackClick({ clickName: this.upsellEventName["chat"]["reject"] })
      this.goToChance()
      return
    }

    this.internalTracker.trackClick({ clickName: this.upsellEventName["chat"]["accept"] })

    if (typeof callbackFn === "function") {
      callbackFn()
    } else if (typeof this.purchase === "function") {
      this.purchase()
    }
  },

  handleRejectOffer: function (callbackFn) {
    let eventName =
      this.upsellEventName &&
      this.upsellEventName[upsellOfferType] &&
      this.upsellEventName[upsellOfferType]["reject"]

    if (eventName) {
      this.internalTracker.trackClick({ clickName: eventName })
    }

    if (typeof callbackFn === "function") {
      callbackFn()
    } else if (typeof this.skipCurrentUpsellOffer === "function") {
      this.skipCurrentUpsellOffer()
    }
  },

  async purchase() {
    this.isLoading = true

    // Try to purchase with a saved card without redirection to upsell payments page
    try {
      const checkoutSession = await createPrimerCheckoutSession()

      await this.store.trackStatusForAnalytics("INITIATE_CHECKOUT")

      this.isLoading = false

      if (checkoutSession && checkoutSession.instantlyCharged) {
        this.goToThankYou(checkoutSession)
      } else {
        this.goToPayments()
      }
    } catch (e) {
      console.error(e)
      this.goToPaymentsFail()
      return
    }
  },

  async skipCurrentUpsellOffer() {
    const skippedOffer = pipelineConfig.offers.find(
      (offer) => offer.type && offer.type.toLowerCase() === upsellOfferType.toLowerCase(),
    )

    // Store skipped upsells to use this data for different upsells pages logic
    this.store.values.skippedUpsellOffers = [
      ...this.store.values.skippedUpsellOffers,
      upsellOfferType.toLowerCase(),
    ]

    // We need skippedOfferId because api-diet depends on it
    if (skippedOffer)
      await this.store.push({
        payloadExtraData: { skippedOfferId: skippedOffer.brandOfferId },
        isSilentMode: true,
      })

    this.goToNextUpsellOffer()
  },

  thankYouContinue() {
    this.internalTracker.trackClick({
      clickName: this.upsellEventName[upsellOfferType]["continue"],
    })
    this.goToNextUpsellOffer()
  },

  //

  goToNextUpsellOffer() {
    const url = this.getNextUpsellOfferUrl()
    if (url) window.location = this.getNextUpsellOfferUrl()
  },

  goToPayments() {
    window.location = getVariantPageUrl({ pagePath: paymentsPath, pipelineConfig })
  },

  goToPaymentsFail() {
    window.location = getPaymentFailUrl({ pagePath: paymentsFailPath })
  },

  goToThankYou(checkoutSession) {
    window.location = getPaymentSuccessUrl({ checkoutSession, pagePath: paymentsSuccessPath })
  },

  goToChance() {
    window.location = getVariantPageUrl({ pagePath: chancePath, pipelineConfig })
  },

  goToSendOff() {
    const pagePath = sendOffPath || "/send-off/"
    window.location = getVariantPageUrl({ pagePath, pipelineConfig })
  },

  goToOffer(offerPath) {
    if (!offerPath) return
    window.location = getVariantPageUrl({ pagePath: offerPath, pipelineConfig })
  },

  // UI

  getPriceRecurring() {
    return this.store.pricing.getPriceRecurring({ offer: this.upsell })
  },

  getPriceNoDiscountRecurring() {
    return this.store.pricing.getPriceNoDiscountRecurring({ offer: this.upsell })
  },

  getPrice() {
    return this.store.pricing.getPrice({ offer: this.upsell })
  },

  getPriceNoDiscount() {
    return this.store.pricing.getPriceNoDiscount({ offer: this.upsell })
  },

  getGoalWeightDiff() {
    const { weightKg, weightLbs, targetWeightKg, targetWeightLbs } = this.store.values

    if (this.store.isMetric()) {
      return weightKg - targetWeightKg
    } else {
      return weightLbs - targetWeightLbs
    }
  },

  getCurrency() {
    return pipelineConfig.currencyCode
  },

  getPriceDataValue(dataKey) {
    return this.store.pricing.getPriceDataValue({ offer: this.upsell, dataKey })
  },

  getPriceDiscount() {
    return this.store.pricing.getPriceDiscount({
      offer: this.upsell,
    })
  },
})
